/* =====================================================================
 * api.js — «Доходы и расходы» (ДАШБОРД). Слой данных = АДАПТЕР к бэку.
 *
 * Плъминг идентичен рабочему модулю «Видеопоток»: performRequest + withAuth
 * (Bearer из localStorage) + BaseAddress.CcAdmin, query через URLSearchParams.
 *
 * Реальные эндпоинты (подсистема «scadmin», префикс …/api/scadmin):
 *   GET /api/budget/meta?budget=
 *   GET /api/budget/dashboard?budget=&report=&periodType=&from=&to=&treeSection=&treeDepth=
 *   GET /api/budget/export?budget=&report=&format=xlsx|pdf — файл (бинарный ответ)
 *
 * Адаптеры normalizeMeta/normalizeDashboard приводят ответ бэка к форме,
 * которую ждут компонент и mockData. Чего в контракте нет — null/[]; сырой
 * ответ — _raw. report/tolerance/unit лежат в d.meta (см. normalizeDashboard).
 * ===================================================================== */

import { BaseAddress } from "../../config";
import performRequest from "../../utils/requestHelpers";
import {
  metaResponse,
  dashboardResponse,
  BUDGETS,
  THRESHOLDS,
} from "./mockData";

const USE_MOCK_META = false;
const USE_MOCK_DASHBOARD = false;
const USE_MOCK_EXPORT = false;

const BASE = BaseAddress.CcAdmin; // …/api/scadmin

/* ----------------------------- вспомогательное ---------------------------- */

const withAuth = (option) => {
  const token = localStorage.getItem("token");
  return token
    ? {
        ...option,
        headers: {
          ...(option && option.headers),
          Authorization: "Bearer " + token,
        },
      }
    : option;
};

const toJson = (r) => (r && typeof r.json === "function" ? r.json() : r);

const isset = (v) => v !== undefined && v !== null;

/* «Сводное представление» = бюджет 8: свод формирует Казначейство. */
const BUD_ALIAS = { consolidated: "8" };
const budKey = (b) => (BUD_ALIAS[b] ? BUD_ALIAS[b] : b);

const wait = (v, ms) =>
  new Promise((res) => setTimeout(() => res(v), ms == null ? 260 : ms));

export class ApiError extends Error {
  constructor(status, body) {
    super("api_error");
    this.status = status;
    this.body = body || {};
  }
}

/* -------------------- адаптер META: бэк → контракт фронта ----------------- */
function normalizeMeta(raw) {
  const d = raw && raw.data ? raw.data : raw || {};
  const src = d.periods ? d.periods : [];
  const year = [];
  const quarter = [];
  src.forEach((p) => {
    const item = {
      key: p.key,
      year: p.year,
      is_partial: !!p.is_partial,
      upload_date: p.upload_date,
      sort: p.sort,
      label_ru: p.label_ru,
      label_kk: p.label_kk,
    };
    if (p.type === "quarter") quarter.push(item);
    else year.push(item);
  });
  const bySort = (a, b) =>
    (a.sort || 0) - (b.sort || 0) || String(a.key).localeCompare(String(b.key));
  year.sort(bySort);
  quarter.sort(bySort);
  const partial = year.filter((p) => p.is_partial).slice(-1)[0];

  return {
    budgets: BUDGETS.map((b) => ({ k: b.k, label_key: b.labelKey })),
    periods: { year, quarter },
    quarter_empty_reason: quarter.length
      ? null
      : d.empty_reason
      ? d.empty_reason
      : "no_quarterly_uploads",
    thresholds: d.thresholds ? d.thresholds : THRESHOLDS,
    tolerance: isset(d.integrity_tolerance) ? d.integrity_tolerance : null,
    report_date: partial ? partial.upload_date : null,
    region: d.region,
    grouping: d.grouping,
    unit: d.unit_ru,
    unit_kk: d.unit_kk,
    sections: (d.sections ? d.sections : []).map((s) => ({
      k: s.code,
      level: s.level,
      part: s.part,
      ru: s.name_ru,
      kk: s.name_kk,
    })),
  };
}

/* ----------------- адаптер DASHBOARD: бэк → контракт фронта --------------- *
 * В реальном ответе служебное лежит в d.meta: report {key, upload_date,
 * is_partial}, unit_ru/unit_kk; допуск сверки — в d.integrity.tolerance.
 * Бюджетные секции (summary/balance/income_categories/expense_groups/
 * financing/tree/dynamics/integrity) — на верхнем уровне.
 * ------------------------------------------------------------------------- */
function pick(d, k, dflt) {
  return d && d[k] !== undefined ? d[k] : dflt;
}

function normalizeDashboard(raw, params) {
  const d = raw && raw.data ? raw.data : raw || {};
  const m = d.meta || {};
  const rep = m.report || d.report || {};
  const integrity = pick(d, "integrity", {
    ok: true,
    tolerance: null,
    failed: [],
    missing_sections: [],
  });
  return {
    empty: !!d.empty,
    empty_reason: d.empty_reason || null,
    report: {
      key: rep.key || params.report,
      is_partial: isset(rep.is_partial)
        ? !!rep.is_partial
        : !!params.reportIsPartial,
      date: rep.upload_date || rep.date || params.reportDate || null,
    },
    unit: m.unit_ru || null,
    unit_kk: m.unit_kk || null,
    tolerance: isset(d.tolerance)
      ? d.tolerance
      : isset(integrity.tolerance)
      ? integrity.tolerance
      : isset(d.integrity_tolerance)
      ? d.integrity_tolerance
      : null,
    thresholds: d.thresholds ? d.thresholds : THRESHOLDS,
    integrity,
    summary: pick(d, "summary", null),
    balance: pick(d, "balance", null),
    income_categories: pick(d, "income_categories", []),
    expense_groups: pick(d, "expense_groups", []),
    expense_total: pick(d, "expense_total", null),
    financing: pick(d, "financing", []),
    financing_checks: pick(d, "financing_checks", []),
    tree: pick(d, "tree", []),
    dynamics: pick(d, "dynamics", null),
    _raw: d,
  };
}

/* ------------------------------ реальные вызовы --------------------------- */

async function realMeta(budget) {
  const qs = new URLSearchParams();
  qs.set("budget", budKey(budget || "consolidated"));
  const raw = await performRequest(
    `${BASE}/api/budget/meta?${qs.toString()}`,
    withAuth(),
  );
  return normalizeMeta(await toJson(raw));
}

async function realDashboard(params) {
  const qs = new URLSearchParams();
  qs.set("budget", budKey(params.budget));
  if (params.report) qs.set("report", params.report);
  if (params.periodType) qs.set("periodType", params.periodType);
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.treeSection) qs.set("treeSection", params.treeSection);
  qs.set(
    "treeDepth",
    params.treeDepth == null ? "5" : String(params.treeDepth),
  );
  const raw = await performRequest(
    `${BASE}/api/budget/dashboard?${qs.toString()}`,
    withAuth(),
  );
  return normalizeDashboard(await toJson(raw), params);
}

/* -------------------------------- точки входа ---------------------------- */

/* budget нужен: на бэке мета — по одному бюджету (перезапрос при смене). */
export function fetchDashboardMeta(budget) {
  return USE_MOCK_META ? wait(metaResponse()) : realMeta(budget);
}

/* params: { budget, periodType, report, from, to, treeSection, treeDepth, lang,
            reportIsPartial, reportDate } */
export function fetchDashboard(params) {
  return USE_MOCK_DASHBOARD
    ? wait(dashboardResponse(params))
    : realDashboard(params);
}

/* ------------------------------- экспорт файла --------------------------- *
 * performRequest читает ответ как text() — бинарный xlsx/pdf так испортится,
 * поэтому здесь свой fetch → blob → скачивание через временную ссылку.
 * Имя файла — из Content-Disposition, иначе budget_<budget>_<report>.<format>.
 * ------------------------------------------------------------------------- */
function fileNameFrom(res, fallback) {
  const cd = res.headers.get("Content-Disposition") || "";
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(cd);
  if (star) {
    try {
      return decodeURIComponent(star[1].trim().replace(/"/g, ""));
    } catch (e) {
      /* битая кодировка — берём обычный filename */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(cd);
  return plain ? plain[1].trim() : fallback;
}

function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/* format: 'xlsx' | 'pdf'. Ошибку отдаёт как ApiError (status + тело). */
export async function exportDashboard(params, format) {
  if (USE_MOCK_EXPORT) return wait({ ok: true, format, mock: true }, 900);
  const budget = budKey(params.budget);
  const qs = new URLSearchParams();
  qs.set("budget", budget);
  if (params.report) qs.set("report", params.report);
  if (params.periodType) qs.set("periodType", params.periodType);
  if (params.from) qs.set("from", params.from);
  if (params.to) qs.set("to", params.to);
  if (params.treeSection) qs.set("treeSection", params.treeSection);
  qs.set("format", format);

  const res = await fetch(
    `${BASE}/api/budget/export?${qs.toString()}`,
    withAuth({ credentials: "include" }),
  );
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let body = {};
    try {
      body = text ? JSON.parse(text) : {};
    } catch (e) {
      body = { message: text };
    }
    throw new ApiError(res.status, body);
  }
  const blob = await res.blob();
  const name = fileNameFrom(
    res,
    `budget_${budget}${params.report ? "_" + params.report : ""}.${format}`,
  );
  saveBlob(blob, name);
  return { ok: true, format, file_name: name };
}
